/**
 * Storico del sentiment di mercato (F-177): borsa e crypto sulla stessa
 * finestra, giorno per giorno. Si apre dal pulsante «Storico» della card.
 *
 * ## Solo l'esito e qualche numero
 *
 * La lettura intera — ragioni, fonti, sintesi — resta nella card del giorno.
 * Qui per ogni giorno ci sono direzione, score, confidenza e gli indicatori
 * misurati: è ciò che si **confronta** fra giorni e fra ambiti. Trenta sintesi
 * in fila non si confrontano, si leggono una alla volta.
 *
 * ## L'ordine della pagina è l'ordine delle domande
 *
 *  1. **In sintesi** — i due ambiti in due colonne, sulle stesse righe.
 *  2. **Direzione** — la timeline a tre binari (borsa, crypto, confronto):
 *     il colpo d'occhio su «andavano insieme?».
 *  3. **Score** — le due serie sulla **stessa scala fissa** da -100 a +100.
 *     ⚠️ Non `ValueTrendChart`: quello dà a ogni serie il proprio zero, ed è
 *     giusto per patrimoni diversi; qui due score allo stesso livello devono
 *     stare alla stessa altezza, o il confronto mente.
 *  4. **Termometri** — Fear & Greed e VIX, uno per ambito, in due grafici
 *     separati: unità diverse, e un doppio asse farebbe incrociare le curve
 *     dove si vuole.
 *  5. **Variazione del giorno** — S&P 500 e Bitcoin sulla stessa scala in %.
 *  6. **Tabella** — gli stessi dati, leggibili senza il grafico.
 *
 * Tutti i riquadri condividono l'ascissa (finestra intera, stesse gronde) e
 * la data evidenziata: il puntatore su un giorno lo accende ovunque.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, formatDateTime, formatPercent } from '@/lib/format';
import { Alert, Badge, Card, EmptyState, Spinner, TableWrap } from '@/ui';
import { BriefingHistoryChart, type HistorySeries } from './BriefingHistoryChart';
import {
  SENTIMENT_HISTORY_WINDOWS,
  useSentimentTimeline,
  type SentimentDivergence,
  type SentimentHistoryPoint,
} from './sentiment-api';
import {
  DIRECTION_LABEL,
  DIRECTION_TONE,
  DIVERGENCE_LABEL,
  figureValue,
  signedScore,
  summarizeDomain,
  type DomainSummary,
} from './sentiment-format';
import { SentimentTimeline } from './SentimentTimeline';
import './briefing.css';
import './briefing-history.css';
import './sentiment-history.css';

const WINDOW_LABELS: Record<number, string> = { 7: '7 giorni', 30: '30 giorni', 90: '90 giorni' };

/** Scale fisse: costanti di modulo, così il grafico non ricalcola a ogni render. */
const SCORE_BOUNDS = { lower: -100, upper: 100 };
const FEAR_GREED_BOUNDS = { lower: 0, upper: 100 };

const formatScore = (value: string) => signedScore(Math.round(Number(value)));
const formatLevel = (value: string) => new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(Number(value));
const formatChange = (value: string) => formatPercent(value, 2);

function series(
  label: string,
  kind: HistorySeries['kind'],
  points: readonly SentimentHistoryPoint[],
  value: (point: SentimentHistoryPoint) => string | null,
): HistorySeries {
  return { label, kind, points: points.map((point) => ({ date: point.date, value: value(point) })) };
}

function hasValues(line: HistorySeries): boolean {
  return line.points.some((point) => point.value !== null);
}

function DomainCell({ point }: { point: SentimentHistoryPoint | undefined }) {
  if (!point) return <span className="cell--minor">—</span>;
  if (point.status === 'failed' || !point.direction) return <span className="cell--minor">lettura fallita</span>;
  return (
    <span className="sentiment-history__cell">
      <Badge tone={DIRECTION_TONE[point.direction]}>{DIRECTION_LABEL[point.direction]}</Badge>
      <span className="sentiment-history__score">{signedScore(point.score)}</span>
      <span className="cell--minor">{point.confidence}</span>
      {point.revisionCount > 1 && <span className="cell--minor" title={`${point.revisionCount} letture in giornata`}>· rev. {point.revision}</span>}
    </span>
  );
}

function SummaryTable({ stock, crypto, comparisons }: { stock: DomainSummary; crypto: DomainSummary; comparisons: SentimentDivergence[] }) {
  const rows: Array<[string, (summary: DomainSummary) => string]> = [
    ['Letture riuscite', (summary) => String(summary.readings)],
    ['Score medio', (summary) => signedScore(summary.averageScore)],
    ['Giorni in propensione', (summary) => String(summary.riskOn)],
    ['Giorni in avversione', (summary) => String(summary.riskOff)],
    ['Cambi di direzione', (summary) => String(summary.changes)],
    ['Letture fallite', (summary) => String(summary.failed)],
  ];
  const opposed = comparisons.filter((divergence) => divergence === 'opposed').length;
  const aligned = comparisons.filter((divergence) => divergence === 'aligned').length;
  return (
    <>
      <TableWrap>
        <table className="table table--compact sentiment-history__summary">
          <thead>
            <tr>
              <th scope="col" />
              <th scope="col">Borsa</th>
              <th scope="col">Crypto</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                <td>{value(stock)}</td>
                <td>{value(crypto)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      <p className="briefing-history__note">
        {comparisons.length === 0
          ? 'Nessun giorno con entrambe le letture: il confronto fra ambiti non è possibile in questa finestra.'
          : `Su ${comparisons.length} ${comparisons.length === 1 ? 'giorno confrontabile' : 'giorni confrontabili'}: ${aligned} di pari passo, ${opposed} contrapposti, ${comparisons.length - aligned - opposed} senza una direzione condivisa.`}
      </p>
    </>
  );
}

export function SentimentHistoryPage() {
  const [days, setDays] = useState<number>(30);
  // ⚠️ UNA data evidenziata per tutta la pagina: è ciò che rende i riquadri
  // impilati una cosa sola (stessa scelta dello storico dei giudizi).
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const { data, isPending, error } = useSentimentTimeline(days);

  const head = (
    <div className="briefing-history__head">
      <div>
        <h1>
          Sentiment di mercato<span className="briefing-history__subject"> · storico</span>
        </h1>
        <p className="cell--minor">Borsa e crypto giorno per giorno: l&apos;esito e i numeri misurati, non la valutazione intera.</p>
      </div>
      <div className="briefing-history__actions">
        <div className="briefing-history__windows" role="group" aria-label="Periodo">
          {SENTIMENT_HISTORY_WINDOWS.map((window) => (
            <button
              key={window}
              type="button"
              className={`briefing-history__window${window === days ? ' is-active' : ''}`}
              aria-pressed={window === days}
              onClick={() => setDays(window)}
            >
              {WINDOW_LABELS[window]}
            </button>
          ))}
        </div>
        <Link className="btn btn--ghost" to="/briefing">
          Torna al briefing
        </Link>
      </div>
    </div>
  );

  if (isPending) {
    return (
      <div className="briefing">
        {head}
        <Spinner label="Carico lo storico del sentiment…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="briefing">
        {head}
        <Alert variant="error" title="Storico non disponibile">
          {error instanceof Error ? error.message : 'Errore imprevisto.'}
        </Alert>
      </div>
    );
  }

  const { stock, crypto, comparisons, from, to } = data;
  const domain = { from, to };

  if (stock.length === 0 && crypto.length === 0) {
    return (
      <div className="briefing">
        {head}
        <EmptyState
          icon="📭"
          title="Nessuna lettura nel periodo"
          description={`Fra il ${formatDate(from)} e il ${formatDate(to)} non c'è nessuna lettura del sentiment. Accendi la lettura automatica dalla card nella pagina del briefing, o allarga il periodo.`}
        />
      </div>
    );
  }

  const score = [
    series('Borsa', 'peer-a', stock, (point) => (point.score === null ? null : String(point.score))),
    series('Crypto', 'peer-b', crypto, (point) => (point.score === null ? null : String(point.score))),
  ];
  const fearGreed = series('Fear & Greed (crypto)', 'peer-b', crypto, (point) => figureValue(point, 'Fear & Greed'));
  const vix = series('VIX (borsa)', 'peer-a', stock, (point) => figureValue(point, 'VIX'));
  const moves = [
    series('S&P 500', 'peer-a', stock, (point) => figureValue(point, 'S&P 500')),
    series('Bitcoin 24h', 'peer-b', crypto, (point) => figureValue(point, 'Bitcoin 24h')),
  ].filter(hasValues);

  const stockByDate = new Map(stock.map((point) => [point.date, point]));
  const cryptoByDate = new Map(crypto.map((point) => [point.date, point]));
  const divergenceByDate = new Map(comparisons.map((entry) => [entry.date, entry.divergence]));
  const tableDates = [...new Set([...stock, ...crypto].map((point) => point.date))].sort().reverse();
  const last = [...stock, ...crypto].map((point) => point.completedAt).filter((value): value is string => value !== null).sort().at(-1);

  return (
    <div className="briefing">
      {head}

      <Card title="In sintesi" subtitle={`Dal ${formatDate(from)} al ${formatDate(to)}`}>
        <SummaryTable
          stock={summarizeDomain(stock)}
          crypto={summarizeDomain(crypto)}
          comparisons={comparisons.map((entry) => entry.divergence)}
        />
      </Card>

      <Card title="Direzione giorno per giorno" subtitle="Un riquadro per giorno di calendario; il puntino segna un giorno riletto a mano">
        <SentimentTimeline
          from={from}
          to={to}
          stock={stock}
          crypto={crypto}
          comparisons={comparisons}
          hoveredDate={hoveredDate}
          onHover={setHoveredDate}
        />
      </Card>

      <Card title="Score" subtitle="Da -100 (avversione al rischio) a +100 (propensione), stessa scala per i due ambiti">
        <BriefingHistoryChart
          series={score}
          format={formatScore}
          label="Score del sentiment di borsa e crypto nel periodo"
          domain={domain}
          bounds={SCORE_BOUNDS}
          hoveredDate={hoveredDate}
          onHover={setHoveredDate}
        />
      </Card>

      <Card title="Termometri misurati" subtitle="Numeri raccolti prima del modello, al momento di ciascuna lettura">
        <h3 className="sentiment-history__chart-title">Fear &amp; Greed crypto · 0 paura estrema, 100 euforia</h3>
        {hasValues(fearGreed) ? (
          <BriefingHistoryChart
            series={[fearGreed]}
            format={formatLevel}
            label="Fear & Greed crypto nel periodo"
            domain={domain}
            bounds={FEAR_GREED_BOUNDS}
            height={160}
            hoveredDate={hoveredDate}
            onHover={setHoveredDate}
          />
        ) : (
          <p className="briefing-history__empty">Fear &amp; Greed non misurato in questo periodo.</p>
        )}
        <h3 className="sentiment-history__chart-title">VIX · sotto 20 calma, sopra 30 paura</h3>
        {hasValues(vix) ? (
          <BriefingHistoryChart
            series={[vix]}
            format={formatLevel}
            label="VIX nel periodo"
            domain={domain}
            height={160}
            hoveredDate={hoveredDate}
            onHover={setHoveredDate}
          />
        ) : (
          <p className="briefing-history__empty">VIX non misurato in questo periodo.</p>
        )}
      </Card>

      {moves.length > 0 && (
        <Card
          title="Variazione del giorno"
          subtitle="S&P 500 sulla chiusura precedente, Bitcoin sulle 24 ore, all'ora di ciascuna lettura: stessa scala, finestre diverse"
        >
          <BriefingHistoryChart
            series={moves}
            format={formatChange}
            label="Variazione di S&P 500 e Bitcoin nel periodo"
            domain={domain}
            height={180}
            hoveredDate={hoveredDate}
            onHover={setHoveredDate}
          />
        </Card>
      )}

      <Card title="Giorno per giorno" subtitle="Dal più recente" flush>
        <TableWrap>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Giorno</th>
                <th>Borsa</th>
                <th>Crypto</th>
                <th className="col--secondary">Confronto</th>
                <th className="col--tertiary">VIX</th>
                <th className="col--tertiary">Fear &amp; Greed</th>
              </tr>
            </thead>
            <tbody>
              {tableDates.map((date) => {
                const divergence = divergenceByDate.get(date);
                const stockPoint = stockByDate.get(date);
                const cryptoPoint = cryptoByDate.get(date);
                return (
                  <tr
                    key={date}
                    className={`sentiment-history__row${hoveredDate === date ? ' is-hovered' : ''}`}
                    onMouseEnter={() => setHoveredDate(date)}
                    onMouseLeave={() => setHoveredDate(null)}
                  >
                    <td>{formatDate(date)}</td>
                    <td>
                      <DomainCell point={stockPoint} />
                    </td>
                    <td>
                      <DomainCell point={cryptoPoint} />
                    </td>
                    <td className="col--secondary">{divergence ? DIVERGENCE_LABEL[divergence] : <span className="cell--minor">—</span>}</td>
                    <td className="col--tertiary">{stockPoint?.figures.find((figure) => figure.name === 'VIX')?.display ?? '—'}</td>
                    <td className="col--tertiary">{cryptoPoint?.figures.find((figure) => figure.name === 'Fear & Greed')?.display ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      <p className="briefing__disclaimer">
        Il sentiment è un&apos;opinione del modello con i numeri misurati accanto: contesto per leggere la giornata, non un
        segnale operativo e non una previsione.
        {last && ` Ultima lettura del ${formatDateTime(last)}.`}
      </p>
    </div>
  );
}
